/**
 * ═══════════════════════════════════════════════════════════════════════════
 * G-48 — the web side of the NFC fob, and the ONLY file that knows the plugin
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * The native half lives in the tablet app
 * (`android/app/src/main/java/cc/flowerworld/kiosk/FobNfcPlugin.java`). This is
 * the seam between it and the React code: features ask "is there a reader?" and
 * "read me a fob", never `window.Capacitor`.
 *
 * The house rule (G-27 interaction rule 5, and the owner's 2026-08-04
 * constraint): outside the tablet app there is no reader, so `supported()` is
 * false and every fob affordance is ABSENT — never a dead button, never a
 * broken one. The PIN keeps working for everybody, which is also the fallback
 * for a lost fob (OD-36).
 */

import { t } from "../i18n/t";
import { isNativeShell } from "./shell";

/** What a tap yields. `counter` is present only when the tag reports one. */
export interface FobRead {
  payload: string;
  counter?: number;
}

/** What `identify()` says about a tag we could not use. */
export interface FobTagInfo {
  techList: string;
  id: string;
  ndefCapable: boolean;
  ndefFormattable: boolean;
  ndefType?: string;
  maxSize?: number;
  writable?: boolean;
  counter?: number;
  type2Read?: string;
}

interface FobNfcPlugin {
  isAvailable(): Promise<{ supported: boolean; enabled: boolean }>;
  scan(options?: { timeoutMs?: number }): Promise<FobRead>;
  write(options: { payload: string; timeoutMs?: number }): Promise<{ ok: boolean }>;
  identify(options?: { timeoutMs?: number }): Promise<FobTagInfo>;
  cancel(): Promise<void>;
  hold(): Promise<void>;
  release(): Promise<void>;
}

function plugin(): FobNfcPlugin | undefined {
  if (typeof window === "undefined") return undefined;
  const capacitor = (
    window as Window & { Capacitor?: { Plugins?: Record<string, unknown> } }
  ).Capacitor;
  return capacitor?.Plugins?.FobNfc as FobNfcPlugin | undefined;
}

/** True only where a fob can actually be read: inside the tablet app. */
export function nfcSupported(): boolean {
  return isNativeShell() && plugin() !== undefined;
}

/**
 * Whether the reader is usable RIGHT NOW. Separate from `nfcSupported` because
 * "this device has no NFC" and "NFC is switched off in Settings" need different
 * words in front of a florist, and only one of them is fixable at the counter.
 */
export async function nfcStatus(): Promise<{
  supported: boolean;
  enabled: boolean;
}> {
  const p = plugin();
  if (!isNativeShell() || !p) return { supported: false, enabled: false };
  try {
    return await p.isAvailable();
  } catch {
    return { supported: false, enabled: false };
  }
}

/** Codes the native side rejects with. Anything else is unexpected. */
export type FobErrorCode =
  | "UNSUPPORTED"
  | "DISABLED"
  | "BUSY"
  | "TIMEOUT"
  | "CANCELLED"
  | "INTERRUPTED"
  | "TAG_LOST"
  | "TAG_ERROR"
  | "INVALID_INPUT";

export function fobErrorCode(error: unknown): FobErrorCode | null {
  const code = (error as { code?: unknown } | null)?.code;
  return typeof code === "string" ? (code as FobErrorCode) : null;
}

/**
 * Words for a florist, not for a developer. A timeout is not an error worth
 * shouting about — it means nobody tapped — so callers usually re-arm quietly
 * rather than showing anything at all.
 */
/**
 * WHERE the fob must actually touch — one home for a claim about hardware.
 *
 * Samsung's Galaxy Tab Active5 manual puts the NFC antenna on the BACK, near
 * the top below the camera; the device itself reports nothing (`dumpsys nfc`
 * exposes no antenna info), so this cannot be read at runtime.
 *
 * It is a CONSTANT because the previous version of this fact was three
 * separate string literals all saying "the screen" — invented rather than
 * measured, never checked against the tablet, and they sent the owner hunting
 * for a front antenna that does not exist (2026-09-14). The next tablet model
 * will put it somewhere else; there must be exactly one line to change.
 */
export function fobAntennaHint(): string {
  return t("nfc.antennaHint");
}

export function fobErrorMessage(error: unknown): string {
  switch (fobErrorCode(error)) {
    case "UNSUPPORTED":
      return t("nfc.unsupported");
    case "DISABLED":
      return t("nfc.disabled");
    case "TIMEOUT":
      return t("nfc.timeout");
    case "TAG_LOST":
      // Said here because this is the one place a florist is already holding
      // a fob and getting it wrong.
      return t("nfc.tagLost", { where: fobAntennaHint() });
    case "TAG_ERROR":
      return error instanceof Error && error.message
        ? error.message
        : t("nfc.tagError");
    case "BUSY":
      return t("nfc.busy");
    default:
      return error instanceof Error && error.message
        ? error.message
        : t("nfc.readFailed");
  }
}

/**
 * Keep NFC tags away from the rest of Android for as long as this screen lives.
 *
 * WHY A SCREEN HOLDS THE READER EVEN WHEN IT IS NOT SCANNING. Reader mode is
 * the only thing that stops Android dispatching a tag to whatever app claims
 * NDEF — and this APK claims none, so the tag went to Samsung's tag reader,
 * which opened over the kiosk. It happened twice over: while `verifyFob` was
 * still in flight with the fob still on the antenna, and on every tap once a
 * florist was signed in, because nothing armed the reader on the floor screens.
 *
 * Holding does NOT read anything. A tag arriving with no scan pending is
 * swallowed by the plugin and handed to nobody.
 *
 * Safe everywhere: a no-op outside the app shell, where there is no plugin.
 */
export async function holdFobReader(): Promise<void> {
  try {
    await plugin()?.hold();
  } catch {
    // A tablet with NFC switched off simply never holds. The PIN pad is the
    // fallback for every fob failure and needs no warning about this one.
  }
}

/** Stop holding. Safe to call when no hold was taken. */
export async function releaseFobReader(): Promise<void> {
  try {
    await plugin()?.release();
  } catch {
    // Releasing is best-effort; the activity lifecycle frees it regardless.
  }
}

/** Wait for one tap. Rejects with TIMEOUT if nobody taps in time. */
export async function scanFob(timeoutMs?: number): Promise<FobRead> {
  const p = plugin();
  if (!p) throw Object.assign(new Error("No NFC reader"), { code: "UNSUPPORTED" });
  return p.scan(timeoutMs === undefined ? undefined : { timeoutMs });
}

/** Write one payload to the next fob presented. */
export async function writeFob(payload: string, timeoutMs?: number): Promise<void> {
  const p = plugin();
  if (!p) throw Object.assign(new Error("No NFC reader"), { code: "UNSUPPORTED" });
  await p.write(timeoutMs === undefined ? { payload } : { payload, timeoutMs });
}

/**
 * Identify a tag without writing to it.
 *
 * Kept because "this fob cannot hold NDEF" is a true but useless answer to the
 * question that matters when a box of fobs arrives: WHICH chip is this? Proven
 * necessary on 2026-09-10, when it separated an unusable NfcA-only card from a
 * working NTAG215 in seconds.
 */
export async function identifyTag(timeoutMs?: number): Promise<FobTagInfo> {
  const p = plugin();
  if (!p) throw Object.assign(new Error("No NFC reader"), { code: "UNSUPPORTED" });
  return p.identify(timeoutMs === undefined ? undefined : { timeoutMs });
}

/** Abandon whatever is in flight. Safe to call when nothing is. */
export async function cancelFob(): Promise<void> {
  try {
    await plugin()?.cancel();
  } catch {
    // Cancelling something that already finished is not a failure.
  }
}
